import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as api from '../services/api';
import { setAuthToken, clearAuthToken, setPatientSession, getIsPatientSession, getPatientUuid, getPatientFirstName, getPatientLastName } from '../utils/auth';
import { RootState } from '../store';
import { UserProfile } from '../types/user';

export interface UserState {
  loading: boolean;
  profile: UserProfile | null;
  isAllowedAdmin: boolean | null;
  isPatientSession: boolean;
}

const _isPatientSession = getIsPatientSession();

const initialState: UserState = {
  loading: false,
  profile: _isPatientSession
    ? {
        id: 0,
        first_name: getPatientFirstName(),
        last_name: getPatientLastName(),
        is_patient_session: true,
        patient_uuid: getPatientUuid(),
      }
    : null,
  isAllowedAdmin: null,
  isPatientSession: _isPatientSession,
};

export const login = createAsyncThunk(
  'user/login',
  async (opts: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const res = await api.login(opts.email, opts.password);
      if (res?.data?.auth_token) setAuthToken(res.data.auth_token);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.data || err);
    }
  }
);

export const fetchProfile = createAsyncThunk(
  'user/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      return await api.getProfile();
    } catch (err: any) {
      return rejectWithValue({ message: err?.message || 'Request failed' });
    }
  }
);

export const patientLogin = createAsyncThunk(
  'user/patientLogin',
  async (
    opts: { patient_uuid: string; password: string; totp_code?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.patientLogin(opts);
      if (res?.auth_token) setAuthToken(res.auth_token);
      if (res?.patient_uuid) setPatientSession(res.patient_uuid, res.first_name, res.last_name);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.data || err);
    }
  }
);

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setProfile: (state, action) => {
      state.profile = action.payload;
      state.isAllowedAdmin = action.payload?.isAllowedAdmin ?? null;
    },
    logout: (state) => {
      clearAuthToken();
      state.profile = null;
      state.isAllowedAdmin = null;
      state.isPatientSession = false;
    },
    setPatientSessionState: (
      state,
      action: { payload: { patientUuid: string; firstName: string; lastName: string } }
    ) => {
      setPatientSession(action.payload.patientUuid);
      state.isPatientSession = true;
      state.profile = {
        id: 0,
        first_name: action.payload.firstName,
        last_name: action.payload.lastName,
        is_patient_session: true,
        patient_uuid: action.payload.patientUuid,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        const d = action.payload?.data;
        if (d?.user) {
          state.profile = {
            id: d.user.id,
            email: d.user.email,
            first_name: d.user.first_name || '',
            last_name: d.user.last_name || '',
            group_name: d.user.group_name ?? null,
          };
        }
        if (action.payload?.data?.isAllowedAdmin !== undefined) {
          state.isAllowedAdmin = action.payload.data.isAllowedAdmin;
        }
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        const data = action.payload?.data;
        if (data?.user) {
          state.profile = {
            id: data.user.id,
            username: data.user.username ?? '',
            email: data.user.email ?? '',
            first_name: data.user.first_name ?? '',
            last_name: data.user.last_name ?? '',
            group_name: data.user.group_name ?? null,
          };
        }
        if (data?.isAllowedAdmin !== undefined) {
          state.isAllowedAdmin = data.isAllowedAdmin;
        }
      })
      .addCase(patientLogin.fulfilled, (state, action) => {
        const d = action.payload;
        if (d?.auth_token) {
          state.isPatientSession = true;
          state.profile = {
            id: 0,
            first_name: d.first_name ?? '',
            last_name: d.last_name ?? '',
            is_patient_session: true,
            patient_uuid: d.patient_uuid ?? null,
          };
        }
      });
  },
});

export const { setProfile, logout, setPatientSessionState } = slice.actions;

export const UserSelectors = () => {
  return {
    profile: (s: RootState) => s.user.profile,
    isAllowedAdmin: (s: RootState) => s.user.isAllowedAdmin,
    isPatientSession: (s: RootState) => s.user.isPatientSession,
  };
};

export default slice.reducer;
